import type VectorLayer from 'ol/layer/Vector'
import type VectorSource from 'ol/source/Vector'

import { createNonBlockingRenderByTime } from '../../renderingQueue'
import { getLayerMetadata, setLayerMetadata } from '../layerMetadata'

import { findFeature } from '../findFeature'

import { updateTrafficAreaFeature } from './utils/updateTrafficAreaFeature'
import { findTrafficAreaMarker } from './utils/findTrafficAreaMarker'

const nonblockingRender = createNonBlockingRenderByTime()

/**
 * Update the selected status of the existent Traffic area features
 */
export async function updateTrafficAreasMapSelection(
  layer: VectorLayer<VectorSource>,
  mapMarkers: uui.domain.ui.map.markers.MapMarkers['trafficProfile'],
  mapStyles: uui.domain.ui.map.markers.MapStyles['trafficProfile'],
  selection: Set<string>,
) {
  // retrieve selected items in the current layer
  const layerSelection = (getLayerMetadata(layer, 'selection') ?? new Set()) as Set<string>

  // if prev and next are empty do nothing
  if (selection.size !== 0 || layerSelection.size !== 0) {
    const trafficAreasToDeselect = new Set(layerSelection)

    nonblockingRender.reset()

    // unite all the markers from the profiles
    const aggregatedMarkers = Object.keys(mapMarkers).reduce<
      Record<string, uui.domain.ui.map.markers.Area>
    >((acc, profileId) => {
      return { ...acc, ...mapMarkers[profileId] }
    }, {})

    for (const markerId of selection) {
      await nonblockingRender.next()

      // remove it from depotId to be deselected
      trafficAreasToDeselect.delete(markerId)

      if (layerSelection.has(markerId)) {
        // depotId was previously selected and it's still selected
        // do nothing
        continue
      }

      const marker = findTrafficAreaMarker(aggregatedMarkers, markerId)
      const feature = findFeature(layer, marker.id)

      if (!feature) {
        throw new Error(
          `Trying to select TrafficArea marker with ID: ${markerId} but no feature exist on the map`,
        )
      }

      // the depotId is a new selection
      updateTrafficAreaFeature(mapStyles, marker, feature, true)
    }

    nonblockingRender.reset()

    for (const markerId of trafficAreasToDeselect) {
      await nonblockingRender.next()

      const marker = findTrafficAreaMarker(aggregatedMarkers, markerId)

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        // fail silently in production when deselecting unknown items
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect TrafficArea marker with ID: ${markerId} but no feature exist on the map`,
          )
        }

        continue
      }

      // deselect the road segment
      updateTrafficAreaFeature(mapStyles, marker, feature, false)
    }
  }

  // update the stored collection
  setLayerMetadata(layer, 'selection', selection)
}
